import { Box, Icon, Text, useBreakpointValue } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';

import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import type { FaceliftDialogProps } from '@/components/Atoms/FaceliftDialog/FaceliftDialog.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';

export interface FaceliftDialogWithExitConfirmationProps extends FaceliftDialogProps {
  enableExitConfirmation?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmCancelLabel?: string;
  confirmExitLabel?: string;
  onCancelExit?: () => void;
  onConfirmExit?: () => void;
  isDirty?: boolean;
  saveDraftAction?: ExitConfirmationDraftAction | null;
}

export interface ExitConfirmationDraftAction {
  label?: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

const FaceliftDialogWithExitConfirmation = ({
  open,
  onOpenChange,
  children,
  enableExitConfirmation = true,
  confirmTitle = 'Are you sure you want to exit the form?',
  confirmDescription = 'If you exit this form before submitting, all entered information will be lost.',
  confirmCancelLabel = 'Cancel',
  confirmExitLabel = 'Exit Form',
  onCancelExit,
  onConfirmExit,
  isDirty = false,
  saveDraftAction,
  ...rest
}: FaceliftDialogWithExitConfirmationProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const confirmActionsTemplateColumns = saveDraftAction
    ? {
        base: '1fr',
        sm: 'repeat(3, minmax(0, 1fr))',
      }
    : {
        base: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
      };
  const dialogWidth = useBreakpointValue({
    base: '90vw',
    md: 440,
  });
  const isSavingDraft = Boolean(saveDraftAction?.loading);

  useEffect(() => {
    if (!open) {
      setIsConfirmOpen(false);
    }
  }, [open]);

  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (enableExitConfirmation && !nextOpen && !isDirty) {
        setIsConfirmOpen(true);
        return;
      }

      onOpenChange?.(nextOpen);
    },
    [enableExitConfirmation, isDirty, onOpenChange]
  );

  const handleCancelExit = useCallback(() => {
    setIsConfirmOpen(false);
    onCancelExit?.();
  }, [onCancelExit]);

  const handleConfirmExit = useCallback(() => {
    setIsConfirmOpen(false);
    onConfirmExit?.();
    onOpenChange?.(false);
  }, [onConfirmExit, onOpenChange]);

  return (
    <>
      <FaceliftDialog open={open} onOpenChange={handleDialogOpenChange} {...rest} showCloseBtn={false}>
        {children}
      </FaceliftDialog>

      <FaceliftDialog
        open={isConfirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleCancelExit();
        }}
        showCloseBtn={false}
        showBackBtn={false}
        sx={{
          width: dialogWidth,
          borderRadius: 15,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={2}
        >
          <Box mb={4}>
            <Icon as={MdInfoOutline} boxSize={16} color="brand.solid" />
          </Box>

          <Box mb={4}>
            <FaceliftText as="h3" fontSize="xl" fontWeight={700} textAlign="center">
              {confirmTitle}
            </FaceliftText>
          </Box>

          <Box mb={4}>
            <Text color="gray.600" textAlign="center" fontSize="md" fontWeight={400}>
              {confirmDescription}
            </Text>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={confirmActionsTemplateColumns}
            width="100%"
            gap={2}
          >
            <FaceliftButton
              variant="outline"
              bg="white"
              color="gray.800"
              border="1px solid"
              width="full"
              onClick={handleCancelExit}
              disabled={isSavingDraft}
            >
              {confirmCancelLabel}
            </FaceliftButton>

            <Show when={!!saveDraftAction}>
              <FaceliftButton
                width="full"
                bg="transparent"
                color="brand.600"
                border="1px solid"
                borderColor="brand.600"
                _hover={{
                  bg: 'brand.50',
                }}
                _dark={{
                  _hover: {
                    bg: 'whiteAlpha.100',
                  },
                }}
                onClick={saveDraftAction?.onClick}
                loading={saveDraftAction?.loading}
                disabled={saveDraftAction?.disabled}
              >
                {saveDraftAction?.label || 'Save to Draft'}
              </FaceliftButton>
            </Show>

            <FaceliftButton
              width="full"
              bg="red.500"
              _hover={{ bg: 'red.600' }}
              color="white"
              onClick={handleConfirmExit}
              disabled={isSavingDraft}
            >
              {confirmExitLabel}
            </FaceliftButton>
          </Box>
        </Box>
      </FaceliftDialog>
    </>
  );
};

export default FaceliftDialogWithExitConfirmation;
